// useFinalCapabilities — Tier-aware resolver for /final.
//
// /final is a read-only executive view aggregating Flow-weaver,
// Atlas and Process Engine snapshots. It does NOT execute anything,
// so the gating story is simpler than processengine: a single
// `finalEnabled` flag plus an AI gate for the narrative composer.

import { useMemo } from 'react';
import { usePlan } from '@/hooks/usePlan';
import { canUse, type PlanTier } from '@/lib/planTiers';
import { IS_FINAL_BUILD } from '@/lib/buildMode';
import { useSystemSwitches } from '@/hooks/useSystemSwitches';

export interface FinalCapabilities {
  buildEnabled: boolean;
  finalEnabled: boolean;
  finalAI: boolean;
  finalMinTier: PlanTier;
  finalAIMinTier: PlanTier;
  currentTier: PlanTier;
}

export function useFinalCapabilities(): FinalCapabilities {
  const { tier, limits } = usePlan();
  const { switches } = useSystemSwitches();
  return useMemo(() => {
    // /final aggregates Atlas content, so the atlas-switch gates it too.
    const systemOn = IS_FINAL_BUILD && switches.atlas;
    const enabledGate = canUse(limits, 'final');
    const aiGate = canUse(limits, 'final-ai');
    return {
      buildEnabled: IS_FINAL_BUILD,
      finalEnabled: systemOn && limits.finalEnabled,
      finalAI: systemOn && limits.finalAI && switches.ai,
      finalMinTier: (enabledGate.requiredTier ?? 'pro') as PlanTier,
      finalAIMinTier: (aiGate.requiredTier ?? 'max') as PlanTier,
      currentTier: tier,
    };
  }, [limits, tier, switches.atlas, switches.ai]);
}
